import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as ViewIcon } from '@/assets/icons/view.icon.svg';
import { ReactComponent as MoreIcon } from '@/assets/icons/kebab.icon.svg';
import { ReactComponent as AscIcon } from '@/assets/icons/asc.icon.svg';
import { ReactComponent as DescIcon } from '@/assets/icons/desc.icon.svg';
import { ReactComponent as EmptyIcon } from '@/assets/icons/empty.icon.svg';
import { ReactComponent as SearchIcon } from '@/assets/icons/search.icon.svg';
import { numberWithCommas, numberWithCommas2 } from '@/utils/helpers';
import useOnClickOutside from '@/hooks/useClickOutside';
import BillDetails from './BillDetail';
import { useSortableData } from '@/hooks/useSortableData';
import MarkBillAsPaid from './MarkBillAsPaid';
import { device } from '@/constants/breakpoints';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { duplicateBill, fetchBills } from '@/services/billsApi';
import toast from 'react-hot-toast';
import ModalLoader from '@/components/ModalLoader';
import useUser from '@/hooks/useUser';
import CreatePinModal from '@/components/CreatePinModal';
import TableLoader from '@/components/TableLoader';
import Pagination from '@/components/Pagination';
import useDebounce from '@/hooks/useDebounce';
import SortingComponent from '@/components/SortingComponent';
import FilterSort from '@/components/FilterSort';
import TableRowsSelect from '@/components/TableRowSelect';

const PartiallyPaidBills = ({ enabledFetch }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = searchParams.get('limit');
  const currentPage = searchParams.get('page');
  const [showMenu, setShowMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showCreatePinModal, setShowCreatePinModal] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState({});
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('none');
  const [filter, setFilter] = useState('none');
  const debouncedsearch = useDebounce(search, 500);
  const ref = useRef();
  const filterRef = useRef();

  useOnClickOutside(ref, () => setShowMenu(false));
  useOnClickOutside(filterRef, () => setShowFilterMenu(false));

  const handleFilterSortApply = useCallback(({ filter }) => {
    setFilter(filter);
  }, []);

  const bills = useQuery({
    queryKey: [
      'bills',
      {
        status: 'partially-paid',
        page: currentPage || 1,
        pageLimit: limit || 10,
        search: debouncedsearch?.trim(),
        sortBy: sortOrder,
        fetch: filter
      }
    ],

    queryFn: fetchBills,
    enabled: enabledFetch
  });

  const handleRowsPerPageChange = (value) => {
    setSearchParams(`tab=partially-paid&page=1&limit=${value}`);
  };

  const onPageChange = (page) => {
    setSearchParams(`tab=partially-paid&page=${page}&limit=${limit || 10}`);
  };

  const { items, requestSort, sortConfig } = useSortableData(bills?.data?.data?.items || []);
  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const handleDuplicateBill = useMutation(({ id }) => duplicateBill(id), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['billsMetrics'] });
      navigate('/make-payments/bills?tab=draft');
      toast.success('Bill duplicated successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  return (
    <PartiallyPaidBillsView>
      <div className="drafts-header">
        <div className="title-view">
          <h3>
            Total: ₦{numberWithCommas(bills?.data?.data?.meta?.totalAmount || '00.00')} (
            {numberWithCommas2(bills?.data?.data?.meta?.totalCount || '00.00')})
          </h3>
        </div>

        <div className="drafts-header__actions">
          <SearchView>
            <input
              type="search"
              placeholder="Search bills"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SearchIcon className="search-icon" />
          </SearchView>
          <SortingComponent sortOrder={sortOrder} onSortChange={setSortOrder} />
          <FilterSort onApply={handleFilterSortApply} />
        </div>
      </div>

      {bills?.isLoading ? (
        <TableLoader />
      ) : items?.length > 0 ? (
        <>
          <div className="drafts-table">
            <div className="drafts-table__header">
              {/* <div className="drafts-table__header__item icon">
                <button>
                  <UncheckIcon />
                </button>
              </div> */}
              <div
                className={`drafts-table__header__item vendor ${getClassNamesFor('vendor')}`}
                onClick={() => requestSort('vendor')}>
                <span>
                  Vendor{' '}
                  <span className="icon">
                    <AscIcon className="up" />
                    <DescIcon className="down" />
                  </span>
                </span>
              </div>
              <div
                className={`drafts-table__header__item bill ${getClassNamesFor(
                  'billOrInvoiceNumber'
                )}`}
                onClick={() => requestSort('billOrInvoiceNumber')}>
                <span>
                  Bill No.{' '}
                  <span className="icon">
                    <AscIcon className="up" />
                    <DescIcon className="down" />
                  </span>
                </span>
              </div>
              {/* <div
                className={`drafts-table__header__item date ${getClassNamesFor('createdAt')}`}
                onClick={() => requestSort('createdAt')}
              >
                <span>
                  Created{' '}
                  <span className="icon">
                    <AscIcon className="up" />
                    <DescIcon className="down" />
                  </span>
                </span>
              </div> */}

              <div
                className={`drafts-table__header__item amount ${getClassNamesFor('amount')}`}
                onClick={() => requestSort('amount')}>
                <span>
                  Amount{' '}
                  <span className="icon">
                    <AscIcon className="up" />
                    <DescIcon className="down" />
                  </span>
                </span>
              </div>
              <div
                className={`drafts-table__header__item amount ${getClassNamesFor('PaidAmount')}`}
                onClick={() => requestSort('paidAmount')}>
                <span>
                  Paid amount{' '}
                  <span className="icon">
                    <AscIcon className="up" />
                    <DescIcon className="down" />
                  </span>
                </span>
              </div>
              <div
                className={`drafts-table__header__item date ${getClassNamesFor('dueDate')}`}
                onClick={() => requestSort('dueDate')}>
                <span>
                  Due Date{' '}
                  <span className="icon">
                    <AscIcon className="up" />
                    <DescIcon className="down" />
                  </span>
                </span>
              </div>
              <div className="drafts-table__header__item action">
                <span></span>
              </div>
            </div>

            {/* ====== Table body ====== */}
            <div className="drafts-table__body">
              {items.map((draft, i) => (
                <div className="drafts-table__body-row" key={draft.id}>
                  {/* <div className="drafts-table__body-row__item icon">
                    <button>
                      <UncheckIcon />
                    </button>
                  </div> */}
                  <div className={`drafts-table__body-row__item vendor`}>
                    <span>{draft?.vendor?.companyName}</span>
                  </div>
                  <div className="drafts-table__body-row__item bill">
                    <span>{draft?.billOrInvoiceNumber}</span>
                  </div>
                  {/* <div className="drafts-table__body-row__item date">
                    <span>{format(new Date(draft?.createdAt), 'do MMM, yyyy')}</span>
                  </div> */}
                  <div className="drafts-table__body-row__item amount">
                    <span>₦{numberWithCommas(draft?.amount || '00.00')}</span>
                  </div>
                  <div className="drafts-table__body-row__item amount">
                    <span>₦{numberWithCommas(draft?.paidAmount || '00.00')}</span>
                  </div>
                  <div className="drafts-table__body-row__item date">
                    <span>{format(new Date(draft?.dueDate), 'do MMM, yyyy')}</span>
                  </div>
                  <div className="drafts-table__body-row__item action">
                    <button
                      className="btn view"
                      onClick={() => {
                        setShowDetails(true);
                        setSelectedBill(draft);
                      }}>
                      <ViewIcon />
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(true);
                        setCurrentIndex(i);
                        setSelectedBill(draft);
                      }}
                      className="btn more">
                      <MoreIcon />
                    </button>

                    {/* ===== Dropdown Menu ====== */}
                    <AnimatePresence>
                      {showMenu && currentIndex === i ? (
                        <motion.div
                          className="menu-actions"
                          ref={ref}
                          initial={{ y: -20 }}
                          animate={{ y: 0 }}
                          exit={{ y: -10, opacity: 0 }}
                          transition={{ duration: 0.2 }}>
                          <button
                            onClick={() => {
                              if (user?.hasTransactionPin) {
                                navigate(`/make-payments/bills/pay?id=${draft?.id}`);
                              } else {
                                setShowCreatePinModal(true);
                              }
                            }}>
                            Make payment
                          </button>
                          {/* {!draft?.connectionId && (
                          )} */}
                          <button
                            onClick={() => {
                              setShowMarkAsPaidModal(true);
                              setShowMenu(false);
                            }}>
                            Mark as paid
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleDuplicateBill.mutate({
                                id: draft?.id
                              });
                            }}>
                            Duplicate bill
                          </button>
                          <button
                            onClick={() => {
                              setShowDetails(true);
                            }}>
                            Review bill
                          </button>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="listing">
            {items?.map((item, i) => (
              <div className="item" key={i}>
                <div className="col1">
                  <span className="name">{item?.vendor?.companyName}</span>
                  <span className="date">{format(new Date(item?.createdAt), 'do MMM, yyyy')}</span>
                  <span className="id">{item?.billOrInvoiceNumber}</span>
                </div>
                <button
                  onClick={() => {
                    setShowMenu(true);
                    setCurrentIndex(i);
                    setSelectedBill(item);
                  }}
                  className="btn more">
                  <MoreIcon />
                </button>
                <span className="amount">₦{numberWithCommas(item?.amount || '00.00')}</span>
                <AnimatePresence>
                  {showMenu && currentIndex === i ? (
                    <motion.div
                      className="menu-actions"
                      ref={ref}
                      initial={{ y: -20 }}
                      animate={{ y: 0 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.2 }}>
                      <button
                        onClick={() => {
                          if (user?.hasTransactionPin) {
                            navigate(`/make-payments/bills/pay?id=${item?.id}`);
                          } else {
                            setShowCreatePinModal(true);
                          }
                        }}>
                        Make payment
                      </button>
                      <button
                        onClick={() => {
                          setShowMarkAsPaidModal(true);
                          setShowMenu(false);
                        }}>
                        Mark as paid
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleDuplicateBill.mutate({
                            id: item?.id
                          });
                        }}>
                        Duplicate bill
                      </button>
                      <button
                        onClick={() => {
                          setShowDetails(true);
                        }}>
                        Review bill
                      </button>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EmptyView>
          <EmptyIcon />
          <h3>Nothing to see here!</h3>
        </EmptyView>
      )}

      <div className="pagination">
        <Pagination
          itemsPerPage={limit || 10}
          totalItems={bills?.data?.data?.meta?.totalCount}
          currentPage={Number(currentPage)}
          onPageChange={onPageChange}
        />

        <TableRowsSelect onChange={handleRowsPerPageChange} />
      </div>

      {/* ====== Details Modal ====== */}
      <BillDetails
        showModal={showDetails}
        setShowModal={setShowDetails}
        bill={selectedBill}
        setShowEditModal={setShowEditModal}
      />
      <MarkBillAsPaid
        showModal={showMarkAsPaidModal}
        setShowModal={setShowMarkAsPaidModal}
        bill={selectedBill}
      />

      <CreatePinModal
        showModal={showCreatePinModal}
        setShowModal={setShowCreatePinModal}
        billId={selectedBill?.id}
      />

      {handleDuplicateBill.isLoading && <ModalLoader message="Duplicating bill..." />}
    </PartiallyPaidBillsView>
  );
};

export default PartiallyPaidBills;

const PartiallyPaidBillsView = styled.div`
  width: 100%;
  min-height: 50vh;

  .drafts-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media ${device.phone} {
      display: flex;
      flex-direction: column-reverse;
      align-items: flex-start;
    }

    .title-view {
      h3 {
        font-size: 0.875rem;
        font-weight: 500;
        color: ${(props) => props.theme.colors?.secondary};
      }
    }

    .drafts-header__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      height: 40px;
      position: relative;

      @media ${device.phone} {
        margin-bottom: 20px;
      }

      .action-btn {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        border: none;
        background-color: ${(props) => props.theme.colors?.layer2};
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 20px;
        cursor: pointer;

        &:hover {
          background-color: ${(props) => props.theme.colors?.layer2};
        }

        .more-icon {
          width: 20px;
          height: 20px;
        }
      }

      .menu-actions {
        position: absolute;
        top: 42px;
        right: 0;
        background-color: ${(props) => props.theme.colors?.white};
        border-radius: 6px;
        box-shadow: 0px 11.3975px 34.1925px rgba(0, 0, 0, 0.08);
        width: 180px;
        z-index: 100;
        overflow: hidden;

        button {
          width: 100%;
          padding: 16px 24px;
          border: none;
          background-color: ${(props) => props.theme.colors?.white};
          font-size: 0.875rem;
          color: ${(props) => props.theme.colors?.secondary};
          font-weight: 400;
          display: flex;
          align-items: center;
          justify-content: flex-start;

          &:hover {
            background-color: ${(props) => props.theme.colors?.layer2};
          }
        }
      }
    }
  }

  .drafts-table {
    width: 100%;
    margin-top: 20px;

    @media ${device.phone} {
      display: none;
    }

    .drafts-table__header {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-radius: 6px;
      background: ${(props) => props.theme.colors?.layer2};

      .drafts-table__header__item {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-start;

        span {
          font-size: 0.875rem;
          color: ${(props) => props.theme.colors?.secondary};
          font-weight: 600;
          cursor: pointer;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;

          .icon {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin-left: 3px;
          }
        }
      }

      .ascending {
        color: ${({ theme }) => theme.colors?.secondary};

        .up path {
          stroke: ${({ theme }) => theme.colors?.secondary};
          color: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .descending {
        color: ${({ theme }) => theme.colors?.secondary};

        .down path {
          stroke: ${({ theme }) => theme.colors?.secondary};
          color: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .icon {
        flex: 0.3;
      }

      .vendor {
        flex: 2.5;
      }

      .bill {
        flex: 1;
      }

      .date {
        flex: 1.3;
      }

      .amount {
        flex: 1.2;
      }

      .action {
        flex: 0.5;
      }
    }

    .drafts-table__body {
      width: 100%;
      margin-top: 20px;

      .drafts-table__body-row {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-radius: 6px;
        background: ${(props) => props.theme.colors?.white};

        &:nth-child(odd) {
          background-color: ${(props) => props.theme.colors?.layer3};
        }

        .drafts-table__body-row__item {
          flex: 1;
          display: flex;
          align-items: center;

          span {
            font-size: 0.875rem;
            color: ${(props) => props.theme.colors?.secondary};
            font-weight: 400;
          }
        }

        .icon {
          flex: 0.3;
        }

        .vendor {
          flex: 2.5;
        }

        .bill {
          flex: 1;
        }

        .date {
          flex: 1.3;
        }

        .amount {
          flex: 1.2;
        }

        .action {
          flex: 0.5;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          position: relative;

          .btn {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .view {
            margin-right: 20px;
          }

          .menu-actions {
            position: absolute;
            top: 0px;
            right: 0;
            background-color: ${(props) => props.theme.colors?.white};
            border-radius: 6px;
            box-shadow: 0px 11.3975px 34.1925px rgba(0, 0, 0, 0.08);
            width: 180px;
            z-index: 100;
            overflow: hidden;

            button {
              width: 100%;
              padding: 16px 24px;
              border: none;
              background-color: ${(props) => props.theme.colors?.white};
              font-size: 0.875rem;
              color: ${(props) => props.theme.colors?.secondary};
              font-weight: 400;
              display: flex;
              align-items: center;
              justify-content: flex-start;

              &:hover {
                background-color: ${(props) => props.theme.colors?.layer2};
              }
            }
          }
        }
      }
    }

    .drafts-table__pagination {
      width: 100%;
      margin-top: 30px;
    }
  }

  .listing {
    width: 100%;
    display: none;

    @media ${device.phone} {
      display: block;
    }

    .item {
      width: 100%;
      background: #f4f4f4;
      border-radius: 13px;
      padding: 20px;
      margin-top: 20px;
      min-height: 120px;
      position: relative;

      .col1 {
        display: flex;
        flex-direction: column;

        .name {
          font-size: 1rem;
          font-weight: 600;
          color: ${(props) => props.theme.colors?.secondary};
          margin-bottom: 10px;
        }

        .date {
          font-size: 0.875rem;
          font-weight: 400;
          color: ${(props) => props.theme.colors?.black};
          margin-bottom: 5px;
        }

        .id {
          font-size: 0.875rem;
          font-weight: 400;
          color: ${(props) => props.theme.colors?.info};
        }
      }

      .menu-actions {
        position: absolute;
        top: 20px;
        right: 30px;
        background-color: ${(props) => props.theme.colors?.white};
        border-radius: 6px;
        box-shadow: 0px 11.3975px 34.1925px rgba(0, 0, 0, 0.08);
        width: 180px;
        z-index: 100;
        overflow: hidden;

        button {
          width: 100%;
          padding: 16px 24px;
          border: none;
          background-color: ${(props) => props.theme.colors?.white};
          font-size: 0.875rem;
          color: ${(props) => props.theme.colors?.secondary};
          font-weight: 400;
          display: flex;
          align-items: center;
          justify-content: flex-start;

          &:hover {
            background-color: ${(props) => props.theme.colors?.layer2};
          }
        }
      }

      .more {
        position: absolute;
        top: 20px;
        right: 10px;

        svg {
          width: 20px;
          height: 20px;
        }
      }

      .amount {
        position: absolute;
        bottom: 20px;
        right: 10px;
        font-weight: 600;
        color: ${(props) => props.theme.colors?.secondary};
      }
    }
  }

  .pagination {
    width: 100%;
    margin-top: 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const EmptyView = styled.div`
  width: 100%;
  min-height: 45vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  svg {
    width: 200px;
    height: 200px;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 500;
    color: ${(props) => props.theme.colors?.secondary};
    text-align: center;
    margin-top: 10px;
  }
`;

const SearchView = styled.div`
  width: 300px;
  height: 50px;
  position: relative;
  margin-right: 10px;
  margin-top: 0;

  @media ${device.phone} {
    width: calc(100% - 80px);
    margin-top: 0px;
  }

  input {
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.colors?.layer3};
    border-radius: 8px;
    outline: none;
    border: none;
    padding: 12px;
    padding-left: 38px;
    margin: 0;

    &::placeholder {
      color: ${({ theme }) => theme.colors?.placeholder};
      font-size: 0.875rem;
    }
  }

  .search-icon {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);

    path {
      stroke: ${({ theme }) => theme.colors?.placeholder};
    }
  }
`;
